<template>
  <div class="categories-container">
    <!-- Action Button -->
    <div class="action-header">
      <el-button type="primary" @click="showAddModal = true">
        <el-icon><Plus /></el-icon>
        {{ t('categories.add') }}
      </el-button>
    </div>

    <!-- Categories Table -->
    <el-card class="table-container">
      <el-table :data="categories" stripe style="width: 100%">
        <el-table-column prop="name" :label="t('categories.name')" min-width="180" />
        <el-table-column prop="code" :label="t('categories.code')" width="150" />
        <el-table-column prop="description" :label="t('categories.description')" min-width="250" show-overflow-tooltip />
        <el-table-column :label="t('common.actions')" width="120" align="center">
          <template #default="scope">
            <el-button type="primary" text @click="editCategory(scope.row)">
              <el-icon><Edit /></el-icon>
            </el-button>
            <el-button type="danger" text @click="deleteCategory(scope.row)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Add/Edit Category Modal -->
    <el-dialog
      v-model="showAddModal"
      :title="editingCategory ? t('categories.edit') : t('categories.add')"
      width="30%"
    >
      <el-form 
        :model="categoryForm" 
        label-width="120px"
        label-position="top"
      >
        <el-form-item :label="t('categories.name')" required>
          <el-input v-model="categoryForm.name" :placeholder="t('categories.name')" />
        </el-form-item>
        <el-form-item :label="t('categories.code')" required>
          <el-input v-model="categoryForm.code" :placeholder="t('categories.codeHelpText')" />
          <div class="form-help-text">{{ t('categories.codeHelpText') }}</div>
        </el-form-item>
        <el-form-item :label="t('categories.description')">
          <el-input v-model="categoryForm.description" type="textarea" rows="3" :placeholder="t('categories.description')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddModal = false">{{ t('common.cancel') }}</el-button>
          <el-button type="primary" @click="saveCategory">
            {{ editingCategory ? t('common.actions.update') : t('common.actions.add') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import axios from 'axios'
import { API_URLS, API_VERSION } from '@/config'
import { useI18n } from 'vue-i18n'

// Initialize i18n
const { t } = useI18n()

interface Category {
  id: string;
  name: string;
  description?: string;
  code?: string;
  expense_count?: number;
}

const categories = ref<Category[]>([])
const showAddModal = ref(false)
const editingCategory = ref<Category | null>(null)
const categoryForm = ref({
  name: '',
  description: '',
  code: ''
})

// Function to generate a category code from the name
const generateCategoryCode = (name: string): string => {
  if (!name) return '';
  
  // Split the name into words
  const words = name.trim().split(/\s+/);
  
  // If it's just one word, take the first 3-5 characters
  if (words.length === 1) {
    return words[0].slice(0, Math.min(5, words[0].length)).toUpperCase();
  }
  
  // For multiple words, take the first letter of each word 
  // or first 2 letters for longer words
  let code = '';
  words.forEach(word => {
    if (word.length <= 2) {
      code += word.toUpperCase();
    } else if (word.length <= 5) {
      code += word.charAt(0).toUpperCase();
    } else {
      code += word.slice(0, 2).toUpperCase();
    }
  });
  
  // Ensure the code is between 2-10 characters
  return code.slice(0, 10);
}

// Watch for changes to name and update code
watch(() => categoryForm.value.name, (newName) => {
  if (!editingCategory.value || !editingCategory.value.code) {
    categoryForm.value.code = generateCategoryCode(newName);
  }
})

const fetchCategories = async () => {
  try {
    const response = await axios.get(`${API_URLS.categories}/${API_VERSION}/categories`)
    categories.value = response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
    ElMessage.error(t('error.fetchingCategories'))
    
    // Temporary fallback for testing
    categories.value = [
      {
        "id": "1",
        "name": "Food & Beverage",
        "description": "Expenses related to food and drinks",
        "code": "FB",
        "expense_count": 0
      },
      {
        "id": "2",
        "name": "Transportation",
        "description": "Expenses related to travel and transportation",
        "code": "TRANS",
        "expense_count": 0
      },
      {
        "id": "3",
        "name": "Office Supplies",
        "description": "Expenses for office supplies and equipment",
        "code": "OFFICE",
        "expense_count": 0
      }
    ]
  }
}

const editCategory = (category: Category) => {
  editingCategory.value = category
  categoryForm.value = {
    name: category.name,
    description: category.description || '',
    code: category.code || generateCategoryCode(category.name)
  }
  showAddModal.value = true
}

const saveCategory = async () => {
  try {
    // Basic validation
    if (!categoryForm.value.name.trim()) {
      ElMessage.error(t('categories.validation.nameRequired'));
      return;
    }
    
    if (!categoryForm.value.code.trim()) {
      ElMessage.error(t('categories.validation.codeRequired'));
      return;
    }
    
    // Check if code is unique
    const isDuplicate = categories.value.some(cat => 
      cat.code === categoryForm.value.code && 
      (!editingCategory.value || cat.id !== editingCategory.value.id)
    );
    
    if (isDuplicate) {
      ElMessage.error(t('categories.validation.codeDuplicate'));
      return;
    }
    
    if (editingCategory.value) {
      await axios.put(`${API_URLS.categories}/${API_VERSION}/categories/${editingCategory.value.id}`, categoryForm.value)
      ElMessage.success(t('categories.edit') + ' ' + t('common.success'))
    } else {
      await axios.post(`${API_URLS.categories}/${API_VERSION}/categories`, categoryForm.value)
      ElMessage.success(t('categories.add') + ' ' + t('common.success'))
    }
    showAddModal.value = false
    editingCategory.value = null
    categoryForm.value = {
      name: '',
      description: '',
      code: ''
    }
    await fetchCategories()
  } catch (error) {
    console.error('Error saving category:', error)
    ElMessage.error(t('error.savingCategory'))
  }
}

const deleteCategory = async (category: Category) => {
  ElMessageBox.confirm(
    t('categories.deleteConfirmation'),
    t('common.confirm'),
    {
      confirmButtonText: t('common.delete'),
      cancelButtonText: t('common.cancel'),
      type: 'warning',
    }
  )
    .then(async () => {
      try {
        await axios.delete(`${API_URLS.categories}/${API_VERSION}/categories/${category.id}`)
        ElMessage.success(t('categories.deleteSuccess'))
        await fetchCategories()
      } catch (error) {
        console.error('Error deleting category:', error)
        ElMessage.error(t('error.deletingCategory'))
      }
    })
    .catch(() => {
      // User canceled
    })
}

onMounted(() => {
  fetchCategories()
})
</script>

<style scoped>
.categories-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.action-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.table-container {
  margin-bottom: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-help-text {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
  line-height: 1.4;
}
</style> 